import React, { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mic, MicOff, Video, VideoOff, PhoneOff, Loader2, Stethoscope } from "lucide-react";

export default function CareRoom({ appointment, otherName, onClose }) {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.post(`/appointments/${appointment.id}/join`);
        if (active) setSession(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not join the room");
        if (active) onClose(false);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [appointment.id]);

  useEffect(() => {
    if (!session || !session.mock) return;
    let stopped = false;
    navigator.mediaDevices?.getUserMedia({ video: true, audio: true })
      .then((stream) => {
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      }).catch(() => {});
    return () => { stopped = true; streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, [session]);

  const toggleMic = () => setMicOn((v) => { streamRef.current?.getAudioTracks().forEach((t) => (t.enabled = !v)); return !v; });
  const toggleCam = () => setCamOn((v) => { streamRef.current?.getVideoTracks().forEach((t) => (t.enabled = !v)); return !v; });

  const endCall = async () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    try { await api.post(`/appointments/${appointment.id}/complete`); } catch { /* ignore */ }
    onClose(true);
  };

  const other = otherName || (user?.role === "doctor" ? appointment.patient_name : appointment.doctor_name);

  return (
    <Dialog open onOpenChange={() => endCall()}>
      <DialogContent data-testid="care-room-modal" className="max-w-4xl p-0 overflow-hidden border-0 bg-[#041E26] rounded-3xl">
        <div className="p-4 flex items-center gap-2 text-white border-b border-white/10">
          <span className="flame-dot w-2 h-2 rounded-full bg-green-400" />
          <span className="text-sm font-semibold">Virtual Care Room</span>
          <span className="text-white/40 text-sm">· with {other}</span>
        </div>
        <div className="relative aspect-video bg-black">
          {loading ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-[#F59E0B]" /> Connecting to encrypted room…
            </div>
          ) : session?.mock ? (
            <>
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              {!camOn && <div className="absolute inset-0 flex items-center justify-center bg-[#0A2F3C] text-white/60">Camera off</div>}
              <div className="absolute top-4 right-4 w-40 h-28 rounded-xl bg-[#0A2F3C] border border-white/10 flex flex-col items-center justify-center text-center">
                <div className="w-10 h-10 rounded-full bg-[#F59E0B]/20 flex items-center justify-center mb-1"><Stethoscope className="w-5 h-5 text-[#F59E0B]" /></div>
                <span className="text-white/70 text-xs px-2">{other} in room</span>
              </div>
              <div className="absolute bottom-4 left-4 bg-black/40 backdrop-blur px-3 py-1.5 rounded-lg text-white/80 text-xs">Demo room · add a Daily.co key for real HD video</div>
            </>
          ) : (
            <iframe title="Virtual Care Room" allow="camera; microphone; fullscreen; speaker; display-capture"
              src={`${session.room_url}?t=${session.token}`} className="w-full h-full border-0" />
          )}
        </div>
        <div className="p-4 flex items-center justify-center gap-3">
          {session?.mock && !loading && (
            <>
              <button data-testid="cr-mic-btn" onClick={toggleMic} className={`w-12 h-12 rounded-full flex items-center justify-center ${micOn ? "bg-white/10 text-white" : "bg-white text-[#041E26]"}`}>{micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}</button>
              <button data-testid="cr-cam-btn" onClick={toggleCam} className={`w-12 h-12 rounded-full flex items-center justify-center ${camOn ? "bg-white/10 text-white" : "bg-white text-[#041E26]"}`}>{camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}</button>
            </>
          )}
          {!loading && (
            <button data-testid="cr-end-btn" onClick={endCall} className="px-6 h-12 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center gap-2 font-semibold"><PhoneOff className="w-5 h-5" /> End consultation</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
